/* eslint-disable eqeqeq */
import { uniqBy, orderBy } from 'lodash';
import { convHasUnreadMessages } from '../utils/messaging';
import {
    hasAnyUnreadConv,
    mergeIncomingConvs,
    resolveSelectedConv,
    sortConvsByRecency,
} from './messagingReducer.helpers';
import {
    MESSAGING_LOAD_MORE_CONVERSATIONS_REQUEST,
    MESSAGING_LOAD_MORE_CONVERSATIONS_SUCCESS,
    MESSAGING_LOAD_MORE_CONVERSATIONS_FAILURE,
    MESSAGING_NEW_CONV_REQUEST,
    MESSAGING_NEW_CONV_SUCCESS,
    MESSAGING_NEW_CONV_FAILURE,
} from '../actions/actionTypes/messaging-action-types';

const _ = { uniqBy, orderBy };

const initialState = {
    isLoading: false,
    isRefreshing: false,
    pageLoadedOnce: false,
    showPaywallPopup: false,
    convs: [],
    recepients: [],
    inboxIsOpen: false,
    pollingInterval: 10000,
    preflightConvData: null,
    blockedEntities: [],
    sendMessageFailed: false,
    convCursor: null,
    hasMoreConvs: false,
    isLoadingMoreConvs: false,
    suppressAutoSelect: false,
};

const handleLoadingRequest = state => ({
    ...state,
    isLoading: true,
    pageLoadedOnce: false,
});

const handleLoadingFailure = state => ({
    ...state,
    isLoading: false,
    pageLoadedOnce: true,
});

const handleFetchConvsSuccess = (state, action) => {
    if (!action.data.length) {
        // No conversations: still mark the page loaded so the empty-state renders
        // instead of an endless spinner (the success path below only ran for non-empty data).
        return {
            ...state,
            pageLoadedOnce: true,
            isLoading: false,
        };
    }
    const selectedConv = resolveSelectedConv(state, action.data);
    const { convs, headerUnreadNotification } = mergeIncomingConvs(
        state.convs,
        action.data,
        selectedConv,
        state.inboxIsOpen,
    );
    const paginationState = action.paginated
        ? { convCursor: action.nextCursor ?? null, hasMoreConvs: Boolean(action.nextCursor) }
        : {};
    return {
        ...state,
        convs,
        selectedConv,
        pageLoadedOnce: true,
        isLoading: false,
        headerUnreadNotification,
        ...paginationState,
    };
};

const handleLoadMoreConvsRequest = state => ({
    ...state,
    isLoadingMoreConvs: true,
});

const handleLoadMoreConvsFailure = state => ({
    ...state,
    isLoadingMoreConvs: false,
});

const handleLoadMoreConvsSuccess = (state, action) => {
    const existingIds = new Set(state.convs.map(conv => conv.id));
    const newConvs = action.data.filter(conv => !existingIds.has(conv.id));
    return {
        ...state,
        convs: [...state.convs, ...newConvs],
        convCursor: action.nextCursor ?? null,
        hasMoreConvs: Boolean(action.nextCursor),
        isLoadingMoreConvs: false,
    };
};

const handleUpdateHeaderNotification = state => {
    const { selectedConv, convs, inboxIsOpen } = state;
    if (!selectedConv || !convs?.length) {
        return state;
    }
    return {
        ...state,
        headerUnreadNotification: hasAnyUnreadConv(convs, selectedConv, inboxIsOpen),
    };
};

const handleSelectConv = (state, action) => ({
    ...state,
    selectedConv: action.convId,
    newConversation: false,
    suppressAutoSelect: false,
});

const handleNewConvUi = (state, action) => {
    const { newConversation } = action;
    const newState = {
        ...state,
        newConversation,
    };
    if (newConversation) {
        newState.selectedConv = null;
        newState.recepients = [];
    }
    return newState;
};

const handleSendRequest = state => ({
    ...state,
    sendingMessage: true,
    sendMessageFailed: false,
});

const handleNewConvSuccess = (state, action) => ({
    ...state,
    convs: [action.data, ...state.convs],
    selectedConv: action.data.id,
    sendingMessage: false,
    recepients: [],
    newConversation: false,
});

const handleSendFailure = state => ({
    ...state,
    sendingMessage: false,
    sendMessageFailed: true,
});

const handleGetParticipantsSuccess = (state, action) => {
    const { convId, data } = action;
    const updatedConvs = state.convs.map(conv => (
        conv.id == convId ? { ...conv, participants: data } : conv
    ));
    return {
        ...state,
        convs: updatedConvs,
    };
};

const handleFetchMessagesSuccess = (state, action) => {
    const { convId, data } = action;
    const updatedConvs = state.convs.map(conv => (
        conv.id == convId
            ? { ...conv, messages: data.messages, lastRead: data.lastRead }
            : conv
    ));
    return {
        ...state,
        convs: updatedConvs,
        isLoading: false,
    };
};

const handlePollOrSendSuccess = (state, action) => {
    const { convId, data } = action;
    const updatedConvs = state.convs.map(conv => (
        conv.id == convId
            ? {
                ...conv,
                messages: _.orderBy(_.uniqBy([...data.messages, ...conv.messages], 'id'), 'created', 'desc'),
                lastRead: data.lastRead,
            }
            : conv
    ));
    return {
        ...state,
        convs: sortConvsByRecency(updatedConvs),
    };
};

const handleAddRecepient = (state, action) => ({
    ...state,
    recepients: [...state.recepients, action.member],
});

const handleRemoveRecepient = (state, action) => {
    const recepients = [...state.recepients];
    recepients.splice(action.index, 1);
    return {
        ...state,
        recepients,
    };
};

const handleClearRecepients = state => ({
    ...state,
    recepients: [],
});

const handleShowPaywall = state => ({
    ...state,
    showPaywallPopup: true,
});

const handleHidePaywall = state => ({
    ...state,
    showPaywallPopup: false,
});

const handleScrollToBottom = (state, action) => ({
    ...state,
    scrollToBottom: action.scrollToBottom,
});

const handleInboxIsOpen = (state, action) => ({
    ...state,
    inboxIsOpen: action.inboxIsOpen,
});

const handleSetPollingInterval = (state, action) => ({
    ...state,
    pollingInterval: action.pollingInterval,
});

const handleSetLastReadRequest = (state, action) => {
    const { convId, lastRead } = action;
    const { convs } = state;
    let headerUnreadNotification = false;
    const updatedConvs = convs.map(conv => {
        const next = conv.id == convId ? { ...conv, lastRead } : conv;
        if (convHasUnreadMessages(next)) {
            headerUnreadNotification = true;
        }
        return next;
    });
    return {
        ...state,
        convs: updatedConvs,
        headerUnreadNotification,
    };
};

const handleSavePreflightConvData = (state, action) => ({
    ...state,
    preflightConvData: action.data.preflightConvData,
});

const handleClearPreflightConvData = state => ({
    ...state,
    preflightConvData: null,
});

const handleNewReport = state => ({
    ...state,
    reportPending: false,
    reportSuccess: false,
    reportError: false,
});

const handleReportRequest = state => ({
    ...state,
    reportPending: true,
    reportSuccess: false,
    reportError: false,
});

const handleReportSuccess = state => ({
    ...state,
    reportPending: false,
    reportSuccess: true,
    reportError: false,
});

const handleReportError = state => ({
    ...state,
    reportPending: false,
    reportSuccess: false,
    reportError: true,
});

const handleArchiveOrDeleteSuccess = (state, action) => ({
    ...state,
    convs: state.convs.filter(c => c.id !== action.convId),
    selectedConv: null,
    suppressAutoSelect: true,
});

const handleBlockEntitySuccess = (state, action) => ({
    ...state,
    blockedEntities: [...state.blockedEntities, action.blockEntityRequest],
});

const handleFetchBlockedEntitiesSuccess = (state, action) => ({
    ...state,
    blockedEntities: action.data.blockedEntities,
});

const handleUnblockEntitySuccess = (state, action) => {
    const { unblockEntityRequest } = action;
    const blockedEntities = state.blockedEntities.filter(blocked => (
        parseInt(blocked.entityId) !== parseInt(unblockEntityRequest.entityId) ||
            blocked.entityType !== unblockEntityRequest.entityType
    ));
    return {
        ...state,
        blockedEntities,
    };
};

const handlers = {
    'MESSAGING:FETCH_CONVERSATIONS_REQUEST': handleLoadingRequest,
    'MESSAGING:FETCH_MESSAGES_REQUEST': handleLoadingRequest,
    'MESSAGING:FETCH_CONVERSATIONS_FAILURE': handleLoadingFailure,
    'MESSAGING:FETCH_MESSAGES_FAILURE': handleLoadingFailure,
    'MESSAGING:FETCH_CONVERSATIONS_SUCCESS': handleFetchConvsSuccess,
    [MESSAGING_LOAD_MORE_CONVERSATIONS_REQUEST]: handleLoadMoreConvsRequest,
    [MESSAGING_LOAD_MORE_CONVERSATIONS_SUCCESS]: handleLoadMoreConvsSuccess,
    [MESSAGING_LOAD_MORE_CONVERSATIONS_FAILURE]: handleLoadMoreConvsFailure,
    'MESSAGING:UPDATE_HEADER_NOTIFICATION': handleUpdateHeaderNotification,
    'MESSAGING:SELECT_CONV': handleSelectConv,
    'MESSAGING:NEW_CONV_UI': handleNewConvUi,
    [MESSAGING_NEW_CONV_REQUEST]: handleSendRequest,
    'MESSAGING:SEND_MESSAGE_REQUEST': handleSendRequest,
    [MESSAGING_NEW_CONV_SUCCESS]: handleNewConvSuccess,
    [MESSAGING_NEW_CONV_FAILURE]: handleSendFailure,
    'MESSAGING:SEND_MESSAGE_FAILURE': handleSendFailure,
    'MESSAGING:GET_PARTICIPANTS_SUCCESS': handleGetParticipantsSuccess,
    'MESSAGING:FETCH_MESSAGES_SUCCESS': handleFetchMessagesSuccess,
    'MESSAGING:POLL_MESSAGES_SUCCESS': handlePollOrSendSuccess,
    'MESSAGING:SEND_MESSAGE_SUCCESS': handlePollOrSendSuccess,
    'MESSAGING:ADD_RECEPIENT': handleAddRecepient,
    'MESSAGING:REMOVE_RECEPIENT': handleRemoveRecepient,
    'MESSAGING:CLEAR_RECEPIENTS': handleClearRecepients,
    'MESSAGING:SHOW_PAYWALL': handleShowPaywall,
    'MESSAGING:HIDE_PAYWALL': handleHidePaywall,
    'MESSAGING:SCROLL_TO_BOTTOM': handleScrollToBottom,
    'MESSAGING:INBOX_IS_OPEN': handleInboxIsOpen,
    'MESSAGING:SET_POLLING_INTERVAL': handleSetPollingInterval,
    'MESSAGING:SET_LAST_READ_REQUEST': handleSetLastReadRequest,
    'MESSAGING:SAVE_PREFLIGHT_CONV_DATA': handleSavePreflightConvData,
    'MESSAGING:CLEAR_PREFLIGHT_CONV_DATA': handleClearPreflightConvData,
    'MESSAGING:NEW_REPORT': handleNewReport,
    'MESSAGING:REPORT_REQUEST': handleReportRequest,
    'MESSAGING:REPORT_SUCCESS': handleReportSuccess,
    'MESSAGING:REPORT_ERROR': handleReportError,
    'MESSAGING:ARCHIVE_SUCCESS': handleArchiveOrDeleteSuccess,
    'MESSAGING:DELETE_SUCCESS': handleArchiveOrDeleteSuccess,
    'MESSAGING:BLOCK_ENTITY_SUCCESS': handleBlockEntitySuccess,
    'MESSAGING:FETCH_BLOCKED_ENTITIES_SUCCESS': handleFetchBlockedEntitiesSuccess,
    'MESSAGING:UNBLOCK_ENTITY_SUCCESS': handleUnblockEntitySuccess,
};

function messagingReducer(state = initialState, action) {
    const handler = handlers[action.type];
    return handler ? handler(state, action) : state;
}

export default messagingReducer;
